// ─────────────────────────────────────────────────────────────────────────────
// page_serializer.ts — Serialize notebook pages for LLM consumption
//
// Converts a Page (with its blocks, arrows, strokes) into a clean
// SerializedPage that the LLM can reason about.  Handles:
//   • Closed region extraction (query from enclosed content)
//   • Open region (full page content, query inferred)
//   • Content consumption (marks consumed blocks/strokes)
// ─────────────────────────────────────────────────────────────────────────────

import type {
  Page, Block, BlockId, BoundingBox, Point, RelationArrow,
} from '@shared/notebook_model.js';
import type {
  SerializedPage, SerializedBlock, SerializedArrow, RegionType,
} from '@shared/agent_protocol.js';

// ── Geometry helpers ─────────────────────────────────────────────────────────

function bboxContains(outer: BoundingBox, inner: BoundingBox): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

function bboxCenter(b: BoundingBox): Point {
  return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
}

function distance(a: Point, b: Point): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

// ── Block content extraction ─────────────────────────────────────────────────

function blockContent(block: Block): string {
  switch (block.kind) {
    case 'text':
      return block.content;
    case 'latex':
      return block.latexConfirmed ?? block.latexSuggestion ?? '[ink strokes]';
    case 'code':
      return `[code: ${block.language}] ${block.fileRef}`;
    case 'arcane':
      return `[arcane: ${block.arcaneType}]`;
    case 'figure':
      return `[figure: ${block.shapeKind}]`;
    case 'ink':
      return `[ink: ${block.strokeIds?.length ?? 0} strokes]`;
    default:
      return `[${block.kind}]`;
  }
}

// ── Serialize a single block ─────────────────────────────────────────────────

function serializeBlock(block: Block): SerializedBlock {
  return {
    id: block.id,
    kind: block.kind,
    content: blockContent(block),
    bbox: {
      x: Math.round(block.bbox.x),
      y: Math.round(block.bbox.y),
      w: Math.round(block.bbox.width),
      h: Math.round(block.bbox.height),
    },
  };
}

// ── Serialize arrows ─────────────────────────────────────────────────────────

function serializeArrow(arrow: RelationArrow): SerializedArrow {
  return {
    fromId: arrow.fromId,
    toId: arrow.toId,
    type: arrow.type,
    label: arrow.label,
  };
}

// ── Main serializer ──────────────────────────────────────────────────────────

export interface SerializeOptions {
  /** If provided, only serialize blocks inside this bounding box (closed region) */
  regionBounds?: BoundingBox;
  /** Maximum number of blocks to include (token budget proxy) */
  maxBlocks?: number;
  /** Room ID if this page is a house room */
  roomId?: string;
}

/**
 * Serialize a page for LLM consumption.
 * Returns the serialized page and the region type (closed vs open).
 */
export function serializePage(
  page: Page,
  options: SerializeOptions = {},
): { serialized: SerializedPage; regionType: RegionType } {
  const { regionBounds, maxBlocks = 100, roomId } = options;

  let blocks: Block[] = Array.from(page.blocks.values());
  let regionType: RegionType = 'open';

  // If region bounds specified, filter to blocks within the region
  if (regionBounds) {
    const enclosed = blocks.filter(b => bboxContains(regionBounds, b.bbox));
    if (enclosed.length > 0) {
      blocks = enclosed;
      regionType = 'closed';
    }
    // If nothing is enclosed, fall back to full page (open)
  }

  // Sort by vertical position (top to bottom), then left to right
  blocks.sort((a, b) => {
    const dy = a.bbox.y - b.bbox.y;
    return Math.abs(dy) > 20 ? dy : a.bbox.x - b.bbox.x;
  });

  // Trim to budget
  if (blocks.length > maxBlocks) {
    blocks = blocks.slice(0, maxBlocks);
  }

  // Serialize blocks
  const serializedBlocks = blocks
    .filter(b => b.kind !== 'arcane') // exclude trigger arcane from content
    .map(serializeBlock);

  // Serialize arrows that reference included blocks
  const blockIds = new Set(blocks.map(b => b.id));
  const arrows = Array.from(page.arrows.values())
    .filter(a => blockIds.has(a.fromId) || blockIds.has(a.toId))
    .map(serializeArrow);

  return {
    serialized: {
      pageId: page.id,
      title: page.title,
      roomId,
      blocks: serializedBlocks,
      arrows,
    },
    regionType,
  };
}

// ── Extract query from closed region ─────────────────────────────────────────

/**
 * Extract a text query from blocks enclosed in a region around an arcane.
 * Returns the concatenated text content of enclosed blocks, or undefined
 * if no meaningful text is found.
 */
export function extractRegionQuery(
  page: Page,
  regionBounds: BoundingBox,
): string | undefined {
  const blocks = Array.from(page.blocks.values());
  const enclosed = blocks.filter(b =>
    b.kind !== 'arcane' && bboxContains(regionBounds, b.bbox),
  );

  if (enclosed.length === 0) return undefined;

  const texts = enclosed
    .sort((a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x)
    .map(blockContent)
    .filter(t => !t.startsWith('['));

  return texts.length > 0 ? texts.join('\n') : undefined;
}

// ── Find blocks near an arcane ───────────────────────────────────────────────

/**
 * Find blocks within a proximity radius of an arcane block.
 * These are the "nearby content" that gets consumed on invocation.
 */
export function findNearbyBlocks(
  page: Page,
  arcaneBlockId: BlockId,
  radiusPx: number = 200,
): Block[] {
  const arcane = page.blocks.get(arcaneBlockId);
  if (!arcane) return [];

  const center = bboxCenter(arcane.bbox);
  const blocks = Array.from(page.blocks.values());

  return blocks
    .filter(b => b.id !== arcaneBlockId)
    .filter(b => distance(bboxCenter(b.bbox), center) <= radiusPx)
    .sort((a, b) => {
      const da = distance(bboxCenter(a.bbox), center);
      const db = distance(bboxCenter(b.bbox), center);
      return da - db;
    });
}

// ── Consumption (mark blocks as consumed after invocation) ───────────────────

/**
 * Returns block IDs that should be consumed (removed) after an invocation.
 * Consumption = the arcane and nearby content blocks get absorbed.
 */
export function getConsumableBlockIds(
  page: Page,
  arcaneBlockId: BlockId,
  regionBounds?: BoundingBox,
  proximityPx: number = 200,
): BlockId[] {
  const ids: BlockId[] = [arcaneBlockId];

  if (regionBounds) {
    // Closed region: consume everything inside
    for (const block of page.blocks.values()) {
      if (block.id !== arcaneBlockId && bboxContains(regionBounds, block.bbox)) {
        ids.push(block.id);
      }
    }
  } else {
    // Open: consume nearby content blocks
    const nearby = findNearbyBlocks(page, arcaneBlockId, proximityPx);
    for (const b of nearby) {
      ids.push(b.id);
    }
  }

  return ids;
}
